import { useState, useEffect } from "react";
import { Info, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@packages/store/hooks";
import { logout } from "@packages/store/userSlice";
import { useTranslation } from "react-i18next";
import { getEnvironment } from "@packages/utils";

const ProClosedModal = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const proDate = useAppSelector(
    (state) => state.user.teacher?.preferences?.teacherProDate,
  );

  const isLoggedIn = useAppSelector((state) => state.user.token);

  if (getEnvironment() === "DEV") return null;

  if (proDate || !isLoggedIn) return null;

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const handleMailto = () => {
    const subject = encodeURIComponent("Madlen Pro Üyelik Bilgisi");
    const body = encodeURIComponent(
      "Merhaba,\n\nMadlen platformunda yapay zeka dönüşümü hakkında bilgi almak istiyorum.\n\nTeşekkürler.",
    );
    window.open(`mailto:hello@madlen.io?subject=${subject}&body=${body}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop - non-clickable */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative max-w-md w-full mx-4 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header with gradient background - no close button */}
        <div className="relative bg-gradient-to-br from-amber-500 to-amber-600 dark:from-amber-600 dark:to-amber-700 p-6 text-white">
          {/* Decorative background elements */}
          <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
          <div className="absolute -bottom-10 -left-10 w-28 h-28 bg-white/10 rounded-full blur-2xl"></div>

          {/* Header content */}
          <div className="relative flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-full">
              <Info className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">
                {t("proClosedModal.header.title")}
              </h2>
              <p className="text-amber-100 text-sm">
                {t("proClosedModal.header.subtitle")}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-center">
              {t("proClosedModal.trialEndedMessage")}
            </p>

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30 rounded-lg p-4">
              <p className="text-amber-800 dark:text-amber-200 text-sm text-center">
                <span className="font-medium">
                  {t("proClosedModal.pilotSchoolsInfo")}
                </span>
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-lg p-4">
              <p className="text-gray-700 dark:text-gray-300 text-sm mb-3 text-center">
                {t("proClosedModal.contactInfo")}
              </p>

              <div className="flex items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                <Mail className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                <button
                  onClick={handleMailto}
                  className="text-primary-600 dark:text-primary-400 font-medium hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                >
                  {t("proClosedModal.contactButton")}
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
            <button
              onClick={handleMailto}
              className="w-full bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            >
              {t("proClosedModal.footer.contact")}
            </button>
            <button
              onClick={handleLogout}
              className="w-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            >
              {t("proClosedModal.footer.understood")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const STORAGE_KEY = "madlen_daily_info_modal";

export const useDailyInfoModal = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const today = new Date().toDateString();

    const rawData = localStorage.getItem(STORAGE_KEY);
    const data = rawData ? JSON.parse(rawData) : null;

    let shouldShow = false;

    if (!data) {
      // First time: show modal
      shouldShow = true;
    } else {
      const { lastShownDate, shownCount } = data;
      const lastDate = new Date(lastShownDate);
      const currentDate = new Date(today);
      const daysSinceLastShow = Math.floor(
        (currentDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24),
      );

      if (daysSinceLastShow >= 1) {
        if (shownCount < 3) {
          shouldShow = true;
        } else if (daysSinceLastShow >= 5) {
          shouldShow = true;
        }
      }
    }

    if (shouldShow) {
      setIsModalOpen(true);
    }
  }, []);

  const closeModal = () => {
    const today = new Date();
    const rawData = localStorage.getItem(STORAGE_KEY);
    const data = rawData
      ? JSON.parse(rawData)
      : { shownCount: 0, lastShownDate: null };

    const lastDate = new Date(data.lastShownDate);
    const daysSinceLastShow = Math.floor(
      (today.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24),
    );

    let updatedCount = 1;
    if (data.shownCount >= 3 && daysSinceLastShow < 5) {
      updatedCount = 3; // Don't reset yet, just keep at 3 to skip future shows
    } else if (daysSinceLastShow >= 5) {
      updatedCount = 1; // New cycle
    } else if (data.lastShownDate === today.toDateString()) {
      updatedCount = data.shownCount; // Already counted today
    } else {
      updatedCount = data.shownCount + 1;
    }

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        lastShownDate: today.toDateString(),
        shownCount: updatedCount,
      }),
    );

    setIsModalOpen(false);
  };

  return {
    isModalOpen,
    closeModal,
  };
};

export default ProClosedModal;
